// Weekly STP/SWP cycle handling.
//
// The systematic-plan product-details API returns `cycleIds` that mean different
// things per frequency:
//   - DAILY / MONTHLY / QUARTERLY / FORTNIGHTLY -> day-of-month numbers ("1".."31")
//   - WEEKLY -> weekday NAMES ("Monday".."Friday")
//
// The numeric date engine (getNearestValidStpDate / Calender) cannot interpret
// weekday names, so weekly cycleIds are parsed here into dayjs weekday indices
// (Sunday=0 .. Saturday=6) and handled explicitly.

const WEEKDAY_NAME_TO_INDEX: Record<string, number> = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

// Parses cycleIds into a sorted, de-duplicated list of weekday indices (0-6).
// Returns [] when the cycleIds are not weekday names (e.g. numeric day-of-month),
// so numeric frequencies keep their existing behavior untouched.
export const parseWeekdayCycleIds = (
  cycleIds: Array<string | number> | null | undefined,
): number[] => {
  if (!cycleIds || cycleIds.length === 0) return [];
  const indices = cycleIds
    .map((id) => WEEKDAY_NAME_TO_INDEX[String(id).trim().toUpperCase()])
    .filter((idx): idx is number => idx !== undefined);
  return Array.from(new Set(indices)).sort((a, b) => a - b);
};

// True when a date's weekday is one of the allowed weekly cycle weekdays.
export const isWeekdayAllowed = (
  date: Date | number | string,
  allowedWeekdays: number[],
): boolean => {
  if (allowedWeekdays.length === 0) return true;
  const day = new Date(date).getDay();
  return allowedWeekdays.includes(day);
};
